/**
 * Customer block for dispatch job forms (Dashboard Add/Edit dialogs and the
 * Dispatch Jobs report form).
 *
 *   • Exclude from Weightrax — makes the job AFM-only: Site becomes optional
 *     and Customer Search lists every customer.
 *   • Site — the job's loading site / weighbridge (required unless excluded).
 *     Its Weightrax credentials are used for the job, and it scopes the
 *     customer search to Weightrax customers linked to it.
 *   • Customer Search — pick an existing customer; links the job and fills
 *     the read-only Customer name.
 *   • Other — create a new customer inline. The server creates it in the
 *     same transaction as the job with the job's Site and Weightrax setting.
 *     Its JDE code (from JDE, typed in) is required unless the job is excluded.
 *   • Customer — read-only name that will be saved on the job. Legacy jobs
 *     keep their typed name until a customer is picked or created.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LookupField, refToId } from './lookup-field';
import { cn } from '@/lib/utils';

export interface NewCustomerDraft {
  company: string;
  /** From JDE — required unless the job is excluded from Weightrax. */
  jdeCustomerCode: string;
  contactName: { firstName: string; lastName: string };
  email: string;
  mobile: string;
  address: { addressLine1: string; addressLine2: string; city: string; postalCode: string };
}

export interface CustomerPickerValue {
  /** AFM-only job: never sent to Weightrax, Site optional. */
  excludeFromWeightrax: boolean;
  /** Site id, or the populated `{ id, name }` from a loaded job. */
  site: unknown;
  customerRef: string | null;
  customer: string | null;
  newCustomer: NewCustomerDraft | null;
}

export const COMPANY_MAX = 50;

const EMPTY_DRAFT: NewCustomerDraft = {
  company: '',
  jdeCustomerCode: '',
  contactName: { firstName: '', lastName: '' },
  email: '',
  mobile: '',
  address: { addressLine1: '', addressLine2: '', city: '', postalCode: '' },
};

export function customerLabel(c: { jdeCustomerCode?: string | null; company?: string | null }): string {
  const name = c.company || '(no company)';
  return c.jdeCustomerCode ? `${c.jdeCustomerCode}-${name}` : name;
}

/** Validation message for the block, or null when it can be saved. */
export function customerPickerError(v: CustomerPickerValue): string | null {
  if (!v.excludeFromWeightrax && !refToId(v.site)) return 'Pick the site for this job, or exclude it from Weightrax.';
  if (!v.newCustomer) return null;
  const company = v.newCustomer.company.trim();
  if (!company) return 'Enter the new customer’s company name.';
  if (company.length > COMPANY_MAX) return `Company must be ${COMPANY_MAX} characters or fewer.`;
  const code = v.newCustomer.jdeCustomerCode.trim();
  if (!v.excludeFromWeightrax && !code) return 'Enter the new customer’s JDE code, or exclude the job from Weightrax.';
  if (code && !/^[A-Za-z0-9][A-Za-z0-9./-]{0,24}$/.test(code)) return 'JDE code: letters, digits, "-", "/" or "." only, max 25 characters.';
  const email = v.newCustomer.email.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email for the new customer.';
  return null;
}

/** API payload for the job's customer fields. `newCustomer` goes only when
 *  Other is ticked; blank optional fields are dropped. */
export function customerPickerPayload(v: CustomerPickerValue): Record<string, unknown> {
  const site = refToId(v.site);
  const excludeFromWeightrax = v.excludeFromWeightrax;
  if (!v.newCustomer) return { site, excludeFromWeightrax, customerRef: v.customerRef, customer: v.customer || null };
  const d = v.newCustomer;
  const blank = (s: string) => (s.trim() ? s.trim() : undefined);
  return {
    site,
    excludeFromWeightrax,
    newCustomer: {
      company: d.company.trim(),
      jdeCustomerCode: blank(d.jdeCustomerCode)?.toUpperCase() ?? null,
      contactName: { firstName: blank(d.contactName.firstName), lastName: blank(d.contactName.lastName) },
      email: blank(d.email) ?? null,
      mobile: blank(d.mobile) ?? null,
      address: {
        addressLine1: blank(d.address.addressLine1),
        addressLine2: blank(d.address.addressLine2),
        city: blank(d.address.city),
        postalCode: blank(d.address.postalCode),
      },
    },
  };
}

interface CustomerPickerProps {
  value: CustomerPickerValue;
  onChange: (next: CustomerPickerValue) => void;
  /** Height classes for inputs, to match the surrounding form. */
  inputClassName?: string;
}

export function CustomerPicker({ value, onChange, inputClassName = 'h-11 lg:h-9' }: CustomerPickerProps) {
  const other = !!value.newCustomer;
  // What to restore if Other is ticked and then unticked.
  const beforeOther = React.useRef<CustomerPickerValue | null>(null);
  const error = customerPickerError(value);

  const siteId = refToId(value.site);
  const excluded = value.excludeFromWeightrax;
  // Weightrax jobs need a Site before a customer can be chosen.
  const needsSite = !excluded && !siteId;

  function setOther(on: boolean) {
    if (on) {
      beforeOther.current = value;
      onChange({ ...value, customerRef: null, customer: null, newCustomer: { ...EMPTY_DRAFT } });
    } else {
      // Restore the earlier pick, but keep the Site / Weightrax setting chosen now.
      onChange({
        ...(beforeOther.current ?? { customerRef: null, customer: null }),
        site: value.site, excludeFromWeightrax: value.excludeFromWeightrax, newCustomer: null,
      });
    }
  }
  function setSite(site: any) {
    // On a Weightrax job a picked customer may not be linked to the new Site —
    // clear it. A legacy typed name (no link) and an Other draft are kept.
    onChange(value.customerRef && !excluded
      ? { ...value, site, customerRef: null, customer: null }
      : { ...value, site });
  }
  function setExcluded(on: boolean) {
    // Back to a Weightrax job: a picked customer may be excluded or not linked
    // to the Site, so it has to be picked again.
    onChange(!on && value.customerRef
      ? { ...value, excludeFromWeightrax: false, customerRef: null, customer: null }
      : { ...value, excludeFromWeightrax: on });
  }
  function setDraft(patch: (d: NewCustomerDraft) => NewCustomerDraft) {
    if (value.newCustomer) onChange({ ...value, newCustomer: patch(value.newCustomer) });
  }

  // Seed the search trigger's label for a job that is already linked.
  const searchValue = value.customerRef ? { id: value.customerRef, company: value.customer } : null;
  const draft = value.newCustomer;

  return (
    <div className="space-y-3 lg:space-y-2">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <Checkbox checked={excluded} onCheckedChange={(c) => setExcluded(c === true)} />
        Exclude from Weightrax
      </label>
      <div>
        <Label>Site{!excluded && <span className="text-destructive"> *</span>}</Label>
        <LookupField
          endpoint="/master-data/sites"
          labelKey="name"
          value={value.site ?? null}
          required={!excluded}
          placeholder={excluded ? 'Optional' : 'Pick the loading site…'}
          className={inputClassName}
          onChange={(id) => { if (id === null) setSite(null); }}
          onPick={(item) => setSite({ id: String(item.id), name: item.name })}
        />
      </div>
      <div>
        <Label>Customer Search</Label>
        <LookupField
          endpoint="/master-data/customers"
          labelKey="company"
          formatLabel={customerLabel}
          pageSize={100}
          // Never archived customers; on Weightrax jobs only Weightrax
          // customers linked to the Site.
          filter={!excluded && siteId
            ? { archived_ne: 'true', sites: siteId, excludeFromWeightrax_ne: 'true' }
            : { archived_ne: 'true' }}
          value={searchValue}
          disabled={other || needsSite}
          disabledReason={needsSite ? 'Pick a site first' : 'New customer — see below'}
          placeholder="Search by code or company…"
          className={inputClassName}
          onChange={(id) => { if (id === null) onChange({ ...value, customerRef: null, customer: null, newCustomer: null }); }}
          onPick={(item) => onChange({ ...value, customerRef: String(item.id), customer: item.company ?? null, newCustomer: null })}
        />
        <label className={cn('mt-2 flex items-center gap-2 text-sm select-none', needsSite ? 'opacity-50' : 'cursor-pointer')}>
          <Checkbox checked={other} disabled={needsSite} onCheckedChange={(c) => setOther(c === true)} />
          Other — new customer
        </label>
      </div>

      {draft && (
        <div className="rounded-md border bg-muted/30 p-3 space-y-3 lg:space-y-2">
          <div>
            <Label>Company <span className="text-destructive">*</span></Label>
            <Input
              className={inputClassName}
              value={draft.company}
              maxLength={COMPANY_MAX}
              onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))}
              autoFocus
            />
          </div>
          <div>
            <Label>JDE Code{!excluded && <span className="text-destructive"> *</span>}</Label>
            <Input
              className={inputClassName}
              value={draft.jdeCustomerCode}
              maxLength={25}
              placeholder={excluded ? 'Optional' : 'e.g. C41808'}
              onChange={(e) => setDraft((d) => ({ ...d, jdeCustomerCode: e.target.value.toUpperCase() }))}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-2">
            <div><Label>Contact first name</Label><Input className={inputClassName} value={draft.contactName.firstName} onChange={(e) => setDraft((d) => ({ ...d, contactName: { ...d.contactName, firstName: e.target.value } }))} /></div>
            <div><Label>Contact last name</Label><Input className={inputClassName} value={draft.contactName.lastName} onChange={(e) => setDraft((d) => ({ ...d, contactName: { ...d.contactName, lastName: e.target.value } }))} /></div>
            <div><Label>Email</Label><Input className={inputClassName} type="email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} /></div>
            <div><Label>Mobile</Label><Input className={inputClassName} value={draft.mobile} onChange={(e) => setDraft((d) => ({ ...d, mobile: e.target.value }))} /></div>
            <div><Label>Postal address</Label><Input className={inputClassName} value={draft.address.addressLine1} onChange={(e) => setDraft((d) => ({ ...d, address: { ...d.address, addressLine1: e.target.value } }))} /></div>
            <div><Label>Address line 2</Label><Input className={inputClassName} value={draft.address.addressLine2} onChange={(e) => setDraft((d) => ({ ...d, address: { ...d.address, addressLine2: e.target.value } }))} /></div>
            <div><Label>City</Label><Input className={inputClassName} value={draft.address.city} onChange={(e) => setDraft((d) => ({ ...d, address: { ...d.address, city: e.target.value } }))} /></div>
            <div><Label>Postcode</Label><Input className={inputClassName} value={draft.address.postalCode} onChange={(e) => setDraft((d) => ({ ...d, address: { ...d.address, postalCode: e.target.value } }))} /></div>
          </div>
          <p className="text-xs text-muted-foreground">
            {excluded
              ? 'The customer is created with the job, excluded from Weightrax.'
              : 'The customer is created with the job, linked to this site, and sent to Weightrax with this JDE code.'}
          </p>
          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      <div>
        <Label>Customer</Label>
        <Input
          className={cn(inputClassName, 'bg-muted/40')}
          value={(draft ? draft.company : value.customer) ?? ''}
          readOnly
          placeholder="Pick a customer, or tick Other"
          title="Set by Customer Search, or by the new customer's company"
        />
      </div>
    </div>
  );
}
